import { useState, useCallback } from 'react';
import { AlertTriangle, Check, FlaskConical, Info } from 'lucide-react';
import LabBench from '../../LabBench';
import { TestTubeRack } from '@/components/lab';
import InfoBox from '../../InfoBox';

// Educational content for each step
const stepInfoContent: Record<number, {
  title: string;
  content: string;
  whatHappens: string;
  observe: string[];
  equation?: string;
}> = {
  1: {
    title: 'Safety First - Chemical Hazards',
    content: 'Working with cation analysis involves several hazardous chemicals. HCl and HNO₃ are strong acids that can cause chemical burns. NH₄OH releases ammonia gas which irritates the respiratory system. AgNO₃ causes permanent dark stains on skin due to reduction to metallic silver. DMG (dimethylglyoxime) is flammable and should be kept away from heat sources.',
    whatHappens: 'You are acknowledging the safety protocols required for this experiment. These chemicals are handled in small quantities but still require proper precautions including eye protection and working in a well-ventilated area.',
    observe: [
      'Always wear safety goggles when handling chemicals',
      'Work in a fume hood when using NH₄OH',
      'Avoid skin contact with AgNO₃ - it stains!',
      'Wash hands thoroughly after the experiment'
    ]
  },
  2: {
    title: 'Understanding Cations',
    content: 'A cation is a positively charged ion formed when an atom loses one or more electrons. Metals readily form cations because they have low ionization energies - the energy required to remove an electron. The charge on the cation equals the number of electrons lost.',
    whatHappens: 'You are learning about the four cations you will identify: Cu²⁺ (copper loses 2 electrons), Ag⁺ (silver loses 1 electron), Ni²⁺ (nickel loses 2 electrons), and Al³⁺ (aluminum loses 3 electrons). Each has distinctive properties that allow identification.',
    observe: [
      'Cu²⁺ solutions are light blue due to [Cu(H₂O)₆]²⁺ complex',
      'Ag⁺ solutions are colorless',
      'Ni²⁺ solutions are pale green',
      'Al³⁺ solutions are colorless'
    ],
    equation: 'M → M^n+ + ne- (metal loses electrons to form cation)'
  },
  3: {
    title: 'Preparing Test Tubes',
    content: 'Proper labeling is essential in qualitative analysis. Each cation requires specific confirmation tests, and mixing up samples would lead to incorrect conclusions. Test tubes should be labeled clearly before adding any solutions.',
    whatHappens: 'You are labeling four test tubes with the cation symbols (Cu²⁺, Ag⁺, Ni²⁺, Al³⁺). Each tube will be used for a specific confirmation test. The solutions are added at approximately 3 mL each to provide enough sample for testing.',
    observe: [
      'Label tubes clearly with permanent marker',
      'Cu²⁺ tube will have light blue solution',
      'Ag⁺ and Al³⁺ tubes will have colorless solutions',
      'Ni²⁺ tube will have pale green solution'
    ]
  }
};

interface Part1SetupProps {
  onStepComplete?: (stepId: string) => void;
  currentStep?: number;
}

export default function Part1Setup({ onStepComplete, currentStep = 1 }: Part1SetupProps) {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [safetyAcknowledged, setSafetyAcknowledged] = useState(false);
  const [cationsLearned, setCationsLearned] = useState(false);
  const [tubesLabeled, setTubesLabeled] = useState(false);

  const handleAcknowledgeSafety = useCallback(() => {
    setSafetyAcknowledged(true);
    if (onStepComplete) onStepComplete('setup-1');
  }, [onStepComplete]);

  const handleLearnCations = useCallback(() => {
    if (safetyAcknowledged) {
      setCationsLearned(true);
      if (onStepComplete) onStepComplete('setup-2');
    }
  }, [safetyAcknowledged, onStepComplete]);

  const handleLabelTubes = useCallback(() => {
    if (cationsLearned) {
      setTubesLabeled(true);
      if (onStepComplete) onStepComplete('setup-3');
    }
  }, [cationsLearned, onStepComplete]);

  // Test tube data for labeled tubes
  const labeledTubes = tubesLabeled ? [
    { id: 'cu', fillLevel: 60, fillColor: '#64B5F6', label: 'Cu²⁺' },
    { id: 'ag', fillLevel: 60, fillColor: '#F5F5F5', label: 'Ag⁺' },
    { id: 'ni', fillLevel: 60, fillColor: '#A5D6A7', label: 'Ni²⁺' },
    { id: 'al', fillLevel: 60, fillColor: '#F5F5F5', label: 'Al³⁺' },
  ] : [];

  return (
    <LabBench
      experimentName="Cation Analysis Setup"
      completedCount={[safetyAcknowledged, cationsLearned, tubesLabeled].filter(Boolean).length}
      totalSteps={3}
      equipment={['Test tube rack', 'Test tubes', 'Cation solutions']}
      labMode="guided"
      currentStep={currentStep}
    >
      <div className="cation-setup">
        {/* Safety Warning */}
        {!safetyAcknowledged && (
          <div className="safety-warning-panel">
            <div className="warning-header">
              <AlertTriangle size={24} />
              <h3>Safety Warning - Chemical Handling</h3>
              <button
                className="step-info-btn"
                onClick={(e) => { e.stopPropagation(); setShowInfo(1); }}
              >
                ?
              </button>
            </div>
            <ul className="safety-list">
              <li>Wear <strong>safety goggles</strong> at all times</li>
              <li>Handle acids (<strong>HCl, HNO₃</strong>) with care - they are corrosive</li>
              <li><strong>NH₄OH</strong> has a strong ammonia odor - work in fume hood</li>
              <li><strong>AgNO₃</strong> stains skin and clothing - avoid contact</li>
              <li><strong>DMG</strong> solution is flammable - keep away from flames</li>
              <li>Wash hands <strong>thoroughly</strong> after the experiment</li>
            </ul>
            <button className="acknowledge-btn" onClick={handleAcknowledgeSafety}>
              <Check size={16} />
              I Understand and Acknowledge
            </button>
          </div>
        )}

        {/* Learn about Cations */}
        {safetyAcknowledged && !cationsLearned && (
          <div className="info-panel">
            <div className="info-header">
              <Info size={24} />
              <h3>Understanding Cations</h3>
              <button
                className="step-info-btn"
                onClick={(e) => { e.stopPropagation(); setShowInfo(2); }}
              >
                ?
              </button>
            </div>
            <div className="cation-info">
              <p>
                A <strong>cation</strong> is a positively charged ion formed when an atom
                loses one or more electrons. Metals typically form cations because they
                have low ionization energies.
              </p>
              <div className="cation-grid">
                <div className="cation-card" style={{ borderColor: '#64B5F6' }}>
                  <span className="cation-symbol">Cu<sup>2+</sup></span>
                  <span className="cation-name">Copper(II)</span>
                  <span className="cation-desc">Light blue solution</span>
                </div>
                <div className="cation-card" style={{ borderColor: '#9E9E9E' }}>
                  <span className="cation-symbol">Ag<sup>+</sup></span>
                  <span className="cation-name">Silver</span>
                  <span className="cation-desc">Colorless solution</span>
                </div>
                <div className="cation-card" style={{ borderColor: '#A5D6A7' }}>
                  <span className="cation-symbol">Ni<sup>2+</sup></span>
                  <span className="cation-name">Nickel(II)</span>
                  <span className="cation-desc">Pale green solution</span>
                </div>
                <div className="cation-card" style={{ borderColor: '#BDBDBD' }}>
                  <span className="cation-symbol">Al<sup>3+</sup></span>
                  <span className="cation-name">Aluminum</span>
                  <span className="cation-desc">Colorless solution</span>
                </div>
              </div>
              <div className="equation-box">
                <span>Example: Cu → Cu<sup>2+</sup> + 2e<sup>-</sup></span>
              </div>
            </div>
            <button className="action-btn" onClick={handleLearnCations}>
              <Check size={16} />
              I Understand - Continue
            </button>
          </div>
        )}

        {/* Label Test Tubes */}
        {cationsLearned && !tubesLabeled && (
          <div className="setup-panel">
            <div className="info-header">
              <FlaskConical size={24} />
              <h3>Prepare Test Tubes</h3>
              <button
                className="step-info-btn"
                onClick={(e) => { e.stopPropagation(); setShowInfo(3); }}
              >
                ?
              </button>
            </div>
            <p className="instruction-text">
              Label 4 test tubes with the cation symbols: <strong>Cu²⁺, Ag⁺, Ni²⁺, Al³⁺</strong>
            </p>
            <div className="empty-rack">
              <TestTubeRack
                tubes={[
                  { id: '1', fillLevel: 0, fillColor: 'transparent', label: '?' },
                  { id: '2', fillLevel: 0, fillColor: 'transparent', label: '?' },
                  { id: '3', fillLevel: 0, fillColor: 'transparent', label: '?' },
                  { id: '4', fillLevel: 0, fillColor: 'transparent', label: '?' },
                ]}
              />
            </div>
            <button className="action-btn" onClick={handleLabelTubes}>
              <Check size={16} />
              Label Test Tubes
            </button>
          </div>
        )}

        {/* Labeled tubes display */}
        {tubesLabeled && (
          <div className="complete-panel">
            <div className="success-header">
              <Check size={24} />
              <h3>Setup Complete!</h3>
            </div>
            <p>Your test tubes are labeled and filled with cation solutions:</p>
            <div className="labeled-rack">
              <TestTubeRack tubes={labeledTubes} />
            </div>
            <div className="status-message success">
              Ready to begin cation confirmation tests. Proceed to Part 2.
            </div>
          </div>
        )}

        {/* Theory Box */}
        <div className="theory-box">
          <h4>Qualitative Analysis</h4>
          <p>
            Qualitative analysis is the identification of substances based on their
            chemical properties and reactions. For cations, we use specific reagents
            that produce characteristic colors or precipitates.
          </p>
        </div>

        <style>{`
          .cation-setup {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
            max-width: 650px;
          }

          .safety-warning-panel {
            width: 100%;
            background: rgba(255, 90, 54, 0.1);
            border: 2px solid #FF5A36;
            border-radius: 12px;
            padding: 20px;
          }

          .warning-header {
            display: flex;
            align-items: center;
            gap: 12px;
            color: #FF5A36;
            margin-bottom: 16px;
          }

          .warning-header h3 {
            margin: 0;
            font-size: 16px;
            flex: 1;
          }

          .safety-list {
            margin: 0 0 20px 0;
            padding-left: 24px;
            font-size: 13px;
            color: var(--text-primary);
            line-height: 1.8;
          }

          .safety-list strong {
            color: #FF5A36;
          }

          .acknowledge-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 24px;
            background: #FF5A36;
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .acknowledge-btn:hover {
            background: #E04A26;
            transform: translateY(-2px);
          }

          .info-panel, .setup-panel, .complete-panel {
            width: 100%;
            background: var(--bg-elevated);
            border-radius: 12px;
            padding: 20px;
          }

          .info-header, .success-header {
            display: flex;
            align-items: center;
            gap: 12px;
            color: var(--primary);
            margin-bottom: 16px;
          }

          .info-header h3, .success-header h3 {
            margin: 0;
            font-size: 16px;
            flex: 1;
          }

          .success-header {
            color: #00A86B;
          }

          .cation-info p {
            font-size: 13px;
            color: var(--text-primary);
            line-height: 1.6;
            margin-bottom: 16px;
          }

          .cation-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
            margin-bottom: 16px;
          }

          .cation-card {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 12px;
            background: var(--bg-dark);
            border-radius: 8px;
            border-left: 4px solid;
          }

          .cation-symbol {
            font-size: 18px;
            font-weight: 700;
            color: var(--text-primary);
          }

          .cation-name {
            font-size: 12px;
            color: var(--text-secondary);
            margin-top: 4px;
          }

          .cation-desc {
            font-size: 11px;
            color: var(--text-muted);
            font-style: italic;
          }

          .equation-box {
            text-align: center;
            padding: 12px;
            background: var(--bg-dark);
            border-radius: 8px;
            font-family: 'Times New Roman', serif;
            font-size: 14px;
            color: var(--text-secondary);
            margin-bottom: 16px;
          }

          .action-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 24px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .action-btn:hover {
            background: var(--primary-dark);
            transform: translateY(-2px);
          }

          .instruction-text {
            font-size: 13px;
            color: var(--text-primary);
            margin-bottom: 16px;
          }

          .empty-rack, .labeled-rack {
            display: flex;
            justify-content: center;
            margin: 20px 0;
          }

          .status-message {
            padding: 12px 16px;
            border-radius: 8px;
            font-size: 13px;
            text-align: center;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.1);
            color: #00A86B;
          }

          .theory-box {
            width: 100%;
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .theory-box p {
            margin: 0;
            font-size: 12px;
            color: var(--text-primary);
            line-height: 1.6;
          }
          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>

        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp2"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}
      </div>
    </LabBench>
  );
}
